import React, { useState } from 'react'
import { Star, PencilIcon, Trash2 } from 'lucide-react'
import { updateCoverImage, deleteImage } from '../../../services/gallery'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { toast } from 'react-toastify'
import ImageEditor from './ImageEditor'
import { AlbumType, Image } from './types/models'
import CookieManager from '../../lib/CookiesManager'
import ImageWithApiKey from '../ImageWithApiKey'
import { X_API_KEY } from '../../../services/api'

interface GalleryContainerProps {
  images: Image[]
  selectedImages: string[]
  onImageSelect: (fileName: string) => void
  onImagesChange: () => void
  selectedAlbum?: AlbumType
}

const GalleryContainer: React.FC<GalleryContainerProps> = ({
  images,
  selectedImages,
  onImageSelect,
  onImagesChange,
}) => {
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<Image | null>(null)
  const [imageToEdit, setImageToEdit] = useState<Image | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)

  const handleImageSelection = (fileName: string) => {
    if (isSelecting) return
    setIsSelecting(true)
    onImageSelect(fileName)
    setTimeout(() => setIsSelecting(false), 100)
  }

  const handleCoverImage = async (image: Image) => {
    try {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()
      if (userId && token) {
        await updateCoverImage(userId, token, image.fileName)
        toast.success(t('cover-image-updated'))
        onImagesChange()
      }
    } catch (error) {
      console.error('Failed to update cover image:', error)
      toast.error(t('cover-image-update-failed'))
    }
  }

  const handleDeleteImage = async (image: Image) => {
    if (image.status !== 0) {
      setImageToDelete(image)
      setShowDeleteModal(true)
    } else {
      await confirmDelete(image)
    }
  }

  const confirmDelete = async (image: Image) => {
    try {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()
      if (userId && token) {
        await deleteImage(userId, token, image.fileName)
        toast.success(t('image-deleted'))
        onImagesChange()
      }
    } catch (error) {
      console.error('Failed to delete image:', error)
      toast.error(t('image-delete-failed'))
    } finally {
      setShowDeleteModal(false)
      setImageToDelete(null)
    }
  }

  const handleBlurImage = (image: Image) => {
    setImageToEdit(image)
  }

  return (
    <>
      {images
        .filter(image => image.status !== 4)
        .map(image => (
          <div
            key={image.fileName}
            className={`relative group bg-grisclair2 rounded-xl overflow-hidden cursor-pointer h-full ${
              selectedImages.includes(image.fileName)
                ? 'border-4 border-redpink'
                : 'border-0 border-transparent hover:border-redpink'
            }`}
            onClick={e => {
              const target = e.target as HTMLElement
              if (target.tagName === 'BUTTON' || target.closest('button'))
                return
              handleImageSelection(image.fileName)
            }}
          >
            <div className='h-full w-full aspect-square overflow-hidden rounded-lg'>
              <ImageWithApiKey imageUrl={image.url} apiKey={X_API_KEY} />

              <div className='absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-100'>
                <div
                  className={`absolute top-2 right-2 space-x-1 sm:space-x-2 flex z-10 transition-all duration-100
      ${
        selectedImages.includes(image.fileName)
          ? 'opacity-100 pointer-events-auto'
          : 'lg:opacity-0 lg:pointer-events-none'
      }
      lg:group-hover:opacity-100 lg:group-hover:pointer-events-auto
    `}
                >
                  <button
                    onClick={e => {
                      event?.preventDefault()
                      e.stopPropagation()
                      handleCoverImage(image)
                    }}
                    className='p-1 sm:p-1.5 md:p-2 bg-white rounded-full hover:bg-yellow-100 transition-colors duration-100'
                    title={t('set-as-cover')}
                  >
                    <Star
                      className={`h-3 w-3 sm:h-4 sm:w-4 ${
                        image.cover ? 'fill-yellow-400' : ''
                      }`}
                    />
                  </button>
                  <button
                    onClick={e => {
                      event?.preventDefault()
                      e.stopPropagation()
                      handleBlurImage(image)
                    }}
                    className='p-1 sm:p-1.5 md:p-2 bg-white rounded-full hover:bg-yellow-100 transition-colors duration-100'
                    title={t('add-blur-to-image')}
                  >
                    <PencilIcon className='h-3 w-3 sm:h-4 sm:w-4' />
                  </button>
                  <button
                    onClick={e => {
                      event?.preventDefault()
                      e.stopPropagation()
                      handleDeleteImage(image)
                    }}
                    className='p-1 sm:p-1.5 md:p-2 bg-white rounded-full hover:bg-yellow-100 transition-colors duration-100'
                    title={t('delete-image')}
                  >
                    <Trash2 className='h-3 w-3 sm:h-4 sm:w-4' />
                  </button>
                </div>
                <div className='rounded-lg'>
                  <label
                    className='absolute bottom-0 left-1/2 transform -translate-x-1/2 cursor-pointer z-10'
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type='checkbox'
                      checked={selectedImages.includes(image.fileName)}
                      onChange={e => {
                        event?.preventDefault()
                        e.stopPropagation()
                        onImageSelect(image.fileName)
                      }}
                      className={`
                      appearance-none
                      h-4 
                      w-4 
                      rounded 
                      border-2
                      transition-all 
                      duration-100 
                      cursor-pointer 
                      ${
                        selectedImages.includes(image.fileName)
                          ? 'bg-[#FF1654] border-[#FF1654]'
                          : 'border-gray-300 bg-white'
                      }
                    `}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        ))}

      {imageToEdit && (
        <ImageEditor
          imageToEdit={{
            url: imageToEdit.url,
            fileName: imageToEdit.fileName,
          }}
          onClose={() => setImageToEdit(null)}
          onSave={() => {
            setImageToEdit(null)
            onImagesChange()
          }}
        />
      )}

      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <Dialog.Panel className='bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl'>
            <Dialog.Title className='text-lg font-medium text-gray-900'>
              {t('confirm-deletion')}
            </Dialog.Title>
            <div className='mt-2'>
              <p className='text-sm text-gray-500'>
                {t('delete-image-status-warning')}
              </p>
            </div>
            <div className='mt-4 flex justify-end space-x-2'>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                onClick={() => setShowDeleteModal(false)}
              >
                {t('cancel')}
              </button>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700'
                onClick={() => imageToDelete && confirmDelete(imageToDelete)}
              >
                {t('delete')}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  )
}

export default GalleryContainer
